import { Scanner } from "@yudiel/react-qr-scanner";
import { CyberpassButton, CyberpassInput } from "../Input";
import React, { useEffect, useState } from "react";
import Modal from "../Modal";
import { useLocalStorage } from "../hooks/useLocalStorage";
import toast from "react-hot-toast";
import QrCodeIcon from "../../public/qrCode.svg?react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../i18n";
import {
  selectSuccess,
  selectError,
  findByPassteamWalletId,
  clearStatus,
} from "../store/customer";

const russianToEnglish = {
  а: "f",
  б: ",",
  в: "d",
  г: "u",
  д: "l",
  е: "t",
  ё: "`",
  ж: ";",
  з: "p",
  и: "b",
  й: "q",
  к: "r",
  л: "k",
  м: "v",
  н: "y",
  о: "g",
  п: "j",
  р: "j",
  с: "c",
  т: "n",
  у: "e",
  ф: "a",
  х: "[",
  ц: "x",
  ч: "i",
  ш: "g",
  щ: "o",
  ъ: "]",
  ы: "s",
  ь: "m",
  э: "z",
  ю: "]",
  я: "q",
};

/**
 * @param {string} s
 * @returns {string}
 */
const translate = (s) => {
  let translated = "";

  for (let char of s) {
    if (russianToEnglish[char]) {
      translated += russianToEnglish[char];
    } else {
      translated += char;
    }
  }

  return translated;
};

/**
 * @typedef {('camera' | 'walletId')} PreferredScanWay
 */

/**
 * @param {object} props
 * @param {boolean} props.isActive
 * @returns {React.Component}
 */
export default function QrCodeScanner({ isActive }) {
  /** @type {[PreferredScanWay, React.Dispatch<PreferredScanWay>]} */
  const [persistedScanWay, setPersistedScanWay] = useLocalStorage(
    "preferredScanWay",
    "camera",
  );
  const [preferredScanWay, setPreferredScanWayState] = useState(() => {
    if (persistedScanWay === "phoneNumber") {
      return "camera";
    }
    return persistedScanWay;
  });

  const setPreferredScanWay = (value) => {
    setPreferredScanWayState(value);
    setPersistedScanWay(value);
  };

  const [qrData, setQrData] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [webcamAvailable, setWebcamAvailable] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const success = useSelector(selectSuccess);
  const error = useSelector(selectError);

  useEffect(() => {
    if (showModal) {
      setPreferredScanWayState(persistedScanWay);
    }
  }, [showModal, persistedScanWay]);

  useEffect(() => {
    if (error) toast.error(t("qr.userNotFound"));
    if (success) {
      toast.success(t("qr.userFound"));
      setQrData("");
      setShowModal(false);
    }
    dispatch(clearStatus());
  }, [success, error]);

  const onScan = (qrCodeValue) => {
    dispatch(findByPassteamWalletId(qrCodeValue));
  };

  useEffect(() => {
    let md = navigator.mediaDevices;
    if (!md || !md.enumerateDevices) {
      setWebcamAvailable(false);
      return;
    }
    md.enumerateDevices().then((devices) => {
      setWebcamAvailable(
        devices.some((device) => "videoinput" === device.kind),
      );
    });
  }, []);

  const tabsContents = {
    camera: webcamAvailable && (
      <Scanner onScan={(result) => onScan(result[0].rawValue)} />
    ),
    walletId: (
      <CyberpassInput
        autoFocus={true}
        id="qrInput"
        name="qrInput"
        type="text"
        placeholder={t("qr.placeholder")}
        value={qrData}
        onChange={(evt) => {
          const value = translate(evt.target.value);
          setQrData(value);
          if (value.length >= 24) {
            onScan(value);
          }
        }}
      />
    ),
  };

  return (
    <>
      <CyberpassButton
        onClick={() => setShowModal(true)}
        style={isActive ? "primary" : "secondary"}
        Icon={QrCodeIcon}
        iconSize="l"
      >
        <p className="text-left">{t("admin.scanQr")}</p>
      </CyberpassButton>
      <Modal
        title=""
        content={
          <div className="min-h-[65vh] flex flex-col hide-svg">
            {tabsContents[preferredScanWay]}
            <span className="flex-grow" />
            <div className="pt-2 flex justify-center flex-grow-0 space-x-2">
              <CyberpassButton
                style={
                  webcamAvailable && preferredScanWay == "camera"
                    ? "primary"
                    : "secondary"
                }
                onClick={() => {
                  if (!webcamAvailable) {
                    toast.error(t("qr.cameraUnavailable"));
                    return;
                  }
                  setPreferredScanWay("camera");
                }}
              >
                {t("qr.camera")}
              </CyberpassButton>
              <CyberpassButton
                style={
                  !webcamAvailable || preferredScanWay == "walletId"
                    ? "primary"
                    : "secondary"
                }
                onClick={() => setPreferredScanWay("walletId")}
              >
                {t("qr.manualInput")}
              </CyberpassButton>
            </div>
          </div>
        }
        showModal={showModal}
        setShowModal={setShowModal}
        hideConfirmButton={true}
      />
    </>
  );
}
